
import Vue from 'vue';
import UserService from '@/services/UserService';
import MeteringService from '@/services/MeteringService';
import AppPortalService from '@/services/AppPortalService';

import { autoComplete, extractErrorMessage } from '@/utils/Util';

export default Vue.extend({
  name: 'UserCreateDialog',

  props: ['tenant'],
  data: (vm: any): any => ({
    dialog: false,
    valid: true,
    alertText: '',
    alertVisible: false,
    alertType: 'error',
    loading: false,
    createEmail: '',
    createPassword: '',
    verifyCreatePassword: '',
    createUserTenant: {},
    createUserTenantRole: 'user',
    createMeteringUser: false,
    createAppPortalUser: false,
    emailRules: [
      (v: string) => !!v || vm.requiredError,
      (v: string) => /.+@.+\..+/.test(v) || vm.emailValidError,
    ],
    showPassword: false,
    requiredSelect: [(v: any) => Object.keys(v).length > 0 || vm.requiredError],
  }),
  async created() {
    if (this.$root.$store.state.session.tenants.length === 1) {
      this.createUserTenant = this.$root.$store.state.session.tenants[0];
    }
    if (this.tenant) {
      this.createUserTenant = this.tenant;
    }
  },
  methods: {
    validate() {
      if (this.createForm.validate()) {
        this.createUser();
      }
    },
    close() {
      this.dialog = false;
    },
    clear() {
      this.createEmail = '';
      this.createPassword = '';
      this.verifyCreatePassword = '';
      this.alertVisible = false;
      this.loading = false;
      this.createMeteringUser = false;
      this.createAppPortalUser = false;
    },
    dismiss() {
      this.clear();
      this.close();
    },
    async createUser() {
      this.loading = true;

      try {
        let tenantId;
        let tenantRole;
        if (Object.keys(this.createUserTenant).length > 0) {
          tenantId = this.createUserTenant.uuid;
          tenantRole = this.createUserTenantRole;
        }

        const user = await UserService.createUser(
          this.createEmail,
          tenantId,
          tenantRole,
          this.createPassword
        );

        if (
          Object.keys(this.createUserTenant).length > 0 &&
          this.createMeteringUser
        ) {
          const meteringUser = await MeteringService.createUser(
            this.createUserTenant.uuid,
            user.uuid
          );
          if (meteringUser?.id === user.uuid) {
            this.meteringActive = true;
          }
        }

        if (
          Object.keys(this.createUserTenant).length > 0 &&
          this.createAppPortalUser
        ) {
          try {
            // Get the tenant default permissions here and use them for creation
            const defaults = await AppPortalService.getTenantDefaultPermissions(this.createUserTenant.uuid);

            await AppPortalService.createUserPermissions(
              this.createUserTenant.uuid,
              user.uuid,
              defaults.dashboardCreate, // dashboardCreate
              defaults.dashboardShare, // dashboardShare
              defaults.propertyFavorites, // propertyFavorites
              defaults.staging, // staging
              defaults.canvasWrite // canvasWrite
            );
          } catch (err: any) {
            console.log(
              'Error creating App Portal permissions on user creation:',
              err,
              err?.response
            );
          }
        }

        this.alertType = 'success';
        this.alertText = this.$t('user.successCreate') as string;
        this.alertVisible = true;

        this.$emit('success', user);
        setTimeout(() => {
          this.dismiss();
        }, 1500);
      } catch (err: any) {
        console.log(err, err.response);
        this.alertType = 'error';
        this.alertText =
          this.$t('user.errorCreate') + ' ' + extractErrorMessage(err);
        this.alertVisible = true;
        this.loading = false;
      }
    },
    autoComplete,

    getServiceState(isActive: boolean) {
      return isActive
        ? this.$t('services.active')
        : this.$t('services.inactive');
    },
    getServiceColor(isActive: boolean) {
      return isActive ? 'green' : 'grey lighten';
    },
  },
  watch: {
    dialog(value: boolean) {
      if (!value) {
        this.clear();
      }
    },
  },
  computed: {
    meteringState(): string {
      return this.getServiceState(this.createMeteringUser);
    },
    meteringStateColor(): string {
      return this.getServiceColor(this.createMeteringUser);
    },
    appPortalState(): string {
      return this.getServiceState(this.createAppPortalUser);
    },
    appPortalStateColor(): string {
      return this.getServiceColor(this.createAppPortalUser);
    },
    permissionRoles(): any {
      return UserService.getRoles();
    },
    createForm(): any {
      return this.$refs.createForm;
    },
    passwordMatch() {
      return () =>
        this.createPassword === this.verifyCreatePassword ||
        this.$t('login.passwordMatch');
    },
    passwordLengthError(): any {
      return this.$t('login.minPasswordLength');
    },
    requiredError(): any {
      return this.$t('required');
    },
    emailValidError(): any {
      return this.$t('login.validEmail');
    },
  },
});
